import { useState } from 'react';
import { PlanGate } from '@/shared/components/PlanGate';
import { useProducts, useSaveProduct, useDeleteProduct, Product, PRODUCT_CATEGORIES } from '@/features/inventory/hooks/useProducts';
import { motion } from 'framer-motion';
import { Package, Plus, Trash2, Edit, AlertTriangle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { format, parseISO, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const Inventory = () => {
  const { data: products = [], isLoading } = useProducts();
  const saveProduct = useSaveProduct();
  const deleteProduct = useDeleteProduct();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Product> | null>(null);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');

  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.brand.toLowerCase().includes(search.toLowerCase()) ||
      p.reference.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || p.category === filterCat;
    return matchSearch && matchCat;
  });

  const openNew = () => {
    setEditing({ category: 'needles', stock: 0, min_stock_alert: 5 });
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!editing?.name) { toast.error('Le nom est obligatoire'); return; }
    saveProduct.mutate(editing as any, {
      onSuccess: () => { setDialogOpen(false); toast.success('Produit enregistré'); },
      onError: () => toast.error('Erreur'),
    });
  };

  const getExpirationStatus = (date: string | null) => {
    if (!date) return null;
    const days = differenceInDays(parseISO(date), new Date());
    if (days < 0) return { label: 'Expiré', color: 'destructive' as const };
    if (days < 30) return { label: `${days}j`, color: 'destructive' as const };
    if (days < 90) return { label: `${days}j`, color: 'secondary' as const };
    return null;
  };

  return (
    <PlanGate
      title={{ fr: 'Inventaire & stocks', en: 'Inventory & stock', es: 'Inventario y stock' }}
      description={{
        fr: 'Gérez vos encres, aiguilles et consommables avec alertes de stock bas. Disponible dès le plan Solo.',
        en: 'Manage your inks, needles and supplies with low-stock alerts. Available from the Solo plan.',
        es: 'Gestiona tus tintas, agujas y suministros con alertas de stock bajo. Disponible desde el plan Solo.',
      }}
    >
    <div className="p-4 lg:p-8 max-w-6xl mx-auto space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-display text-foreground flex items-center gap-3">
            <Package className="text-primary" size={28} />
            Inventaire
          </h1>
          <p className="text-muted-foreground mt-1">Gérez vos produits et stocks</p>
        </div>
        <Button onClick={openNew} className="gap-2">
          <Plus size={16} /> Nouveau produit
        </Button>
      </motion.div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="pl-9" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilterCat('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filterCat === 'all' ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground hover:text-foreground'
            }`}
          >
            Tous
          </button>
          {PRODUCT_CATEGORIES.map(cat => (
            <button
              key={cat.value}
              onClick={() => setFilterCat(cat.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filterCat === cat.value ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground hover:text-foreground'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Products grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((product, i) => {
          const expStatus = getExpirationStatus(product.expiration_date);
          const isLow = product.stock <= product.min_stock_alert;
          return (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className={`glass-card p-4 ${isLow ? 'border border-destructive/30' : ''}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-foreground">{product.name}</h3>
                  <p className="text-sm text-muted-foreground">{product.brand}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(product); setDialogOpen(true); }}>
                    <Edit size={12} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { if (confirm('Supprimer ?')) deleteProduct.mutate(product.id); }}>
                    <Trash2 size={12} className="text-destructive" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                {product.reference && <p className="text-muted-foreground">Réf: {product.reference}</p>}
                {product.lot_number && <p className="text-muted-foreground">Lot: {product.lot_number}</p>}

                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Stock</span>
                  <span className={`font-bold ${isLow ? 'text-destructive' : 'text-foreground'}`}>
                    {isLow && <AlertTriangle size={12} className="inline mr-1" />}
                    {product.stock}
                  </span>
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Badge variant="secondary" className="text-xs">
                    {PRODUCT_CATEGORIES.find(c => c.value === product.category)?.label || product.category}
                  </Badge>
                  {expStatus && (
                    <Badge variant={expStatus.color} className="text-xs">
                      {expStatus.label === 'Expiré' ? '⚠️ Expiré' : `Expire: ${expStatus.label}`}
                    </Badge>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="glass-card p-12 text-center">
          <Package size={40} className="mx-auto mb-3 text-muted-foreground opacity-30" />
          <p className="text-muted-foreground">Aucun produit trouvé</p>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Modifier' : 'Nouveau'} produit</DialogTitle>
            <DialogDescription className="sr-only">Formulaire de produit</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input value={editing?.name || ''} onChange={e => setEditing(prev => ({ ...prev!, name: e.target.value }))} placeholder="Ex: Aiguille 3RL" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Marque</Label>
                <Input value={editing?.brand || ''} onChange={e => setEditing(prev => ({ ...prev!, brand: e.target.value }))} placeholder="Ex: Kwadron" />
              </div>
              <div>
                <Label>Référence</Label>
                <Input value={editing?.reference || ''} onChange={e => setEditing(prev => ({ ...prev!, reference: e.target.value }))} placeholder="Ex: 3RLLT" />
              </div>
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={editing?.category || 'needles'} onValueChange={v => setEditing(prev => ({ ...prev!, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRODUCT_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>N° de lot</Label>
                <Input value={editing?.lot_number || ''} onChange={e => setEditing(prev => ({ ...prev!, lot_number: e.target.value }))} placeholder="Ex: #2025-10-01" />
              </div>
              <div>
                <Label>Stock actuel</Label>
                <Input type="number" value={editing?.stock ?? 0} onChange={e => setEditing(prev => ({ ...prev!, stock: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>
            <div>
              <Label>Seuil d'alerte stock bas</Label>
              <Input type="number" value={editing?.min_stock_alert ?? 5} onChange={e => setEditing(prev => ({ ...prev!, min_stock_alert: parseInt(e.target.value) || 5 }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date d'expiration</Label>
                <Input type="date" value={editing?.expiration_date || ''} onChange={e => setEditing(prev => ({ ...prev!, expiration_date: e.target.value || null }))} />
              </div>
              <div>
                <Label>Date d'ouverture</Label>
                <Input type="date" value={editing?.opening_date || ''} onChange={e => setEditing(prev => ({ ...prev!, opening_date: e.target.value || null }))} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={editing?.notes || ''} onChange={e => setEditing(prev => ({ ...prev!, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saveProduct.isPending}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PlanGate>
  );
};

export default Inventory;
